'use client';

/**
 * CIN-14630: trailer posters must not be fetched from the video provider by the browser.
 *
 * `img.youtube.com` / `vumbnail.com` received every visitor's IP address on page load, before the
 * consent banner had been answered, because the loader passes unknown hosts through untouched.
 * These two are rewritten to our own `/api/video-thumbnail` route, which fetches them server-side.
 *
 * Returns null for anything else, so a new third-party image host is passed through as before
 * rather than silently 404ing — check whether it needs the same treatment when one shows up.
 *
 * An unknown *variant* on a host we do handle is deliberately NOT passed through: every
 * `img.youtube.com` url is rewritten, and the route only serves the `maxresdefault` and `default`
 * filenames, so asking for e.g. `hqdefault.jpg` answers 400 and the poster renders broken. That is
 * the intended trade — falling back to the provider would fix the image by putting the visitor's IP
 * back on YouTube before consent, which is the whole thing this loader exists to prevent. If a
 * poster is missing, add the variant to THUMBNAIL_SOURCES in the route; do not add a fallback here.
 */
function firstPartyVideoThumbnailPath(url) {
    if (url.hostname === 'img.youtube.com') {
        // https://img.youtube.com/vi/<key>/<variant>.jpg
        const [, , key, file] = url.pathname.split('/');
        const variant = file?.replace(/\.jpg$/, '');
        return key && variant ? `/api/video-thumbnail?provider=youtube&key=${key}&variant=${variant}` : null;
    }

    if (url.hostname === 'vumbnail.com') {
        // https://vumbnail.com/<key>.jpg and https://vumbnail.com/<key>_large.jpg
        const file = url.pathname.split('/')[1]?.replace(/\.jpg$/, '');
        if (!file) {
            return null;
        }
        const isLarge = file.endsWith('_large');
        const key = isLarge ? file.slice(0, -'_large'.length) : file;
        return `/api/video-thumbnail?provider=vimeo&key=${key}&variant=${isLarge ? 'large' : 'default'}`;
    }

    return null;
}

export default function cdnLoader({ src, width, quality }) {
    try {
        const u = new URL(src, 'https://dummy.base');
        const host = u.hostname;

        // Deliberately before the width/quality params: the proxy serves the poster as published by
        // the provider and has no resizing to hand them to.
        const videoThumbnailPath = firstPartyVideoThumbnailPath(u);
        if (videoThumbnailPath) {
            return videoThumbnailPath;
        }

        if (host === 'image.tmdb.org') {
            u.hostname = process.env.NEXT_PUBLIC_TMDB_IMAGE_CDN_DOMAIN;
        }

        // Local development serves its own assets from cdn.<CIN_DOMAIN> (LocalStack). Rewriting that
        // to the production image CDN would quietly show production files instead — an asset added
        // locally would look broken while a deleted one would still appear.
        const isLocalCdn = host.endsWith('.test') || host === 'localhost';

        if (!isLocalCdn && (host === 'cdn.' + process.env.CIN_DOMAIN || host === 'cdn.cineamo.com')) {
            u.hostname = process.env.NEXT_PUBLIC_CINEAMO_IMAGE_CDN_DOMAIN;
        }

        if (!isNaN(width)) {
            u.searchParams.set('width', width.toString());
        }
        if (!isNaN(quality)) {
            u.searchParams.set('quality', quality.toString());
        }

        // Check if path has file extension, if not add optimizer=image
        const pathname = u.pathname;
        const hasExtension = /\.[a-zA-Z0-9]+$/.test(pathname);
        if (!hasExtension) {
            u.searchParams.set('optimizer', 'image');
        }

        // fallback: if the image is from another domain — return directly
        return u.toString();
    } catch {
        return src;
    }
}